// // Mock data — dynamically imported below so fixture JSON is never bundled into prod builds.
import { logDebug, logError } from './logger';

const appInstance = import.meta.env.VITE_APP_ENV;

// // Data Analyzer helper functions

export const BreakModeEnum = {
  DEFAULT: 'default', // isTizen & iFrame assumed
  FULLMODE: 'full', // non-Tizen and runs background video with app in iFrame in web browser
  BROWSER: 'browser', // explicitly non-tizen without running video
};

export const isValidBreakMode = (mode) => {
  return Object.values(BreakModeEnum).includes(mode);
};

export function separateQueryStringParameters(queryString) {
  // Create a URLSearchParams object directly from the query string
  // URLSearchParams automatically handles the leading '?' if present
  const searchParams = new URLSearchParams(queryString);

  // Convert URLSearchParams to a regular object
  const results = {};
  for (const [key, value] of searchParams) {
    results[key] = value;
  }

  return results;
}

const getJsonData = async (urlParam) => {
  console.log(`>`);
  console.log(`>>`);
  console.log(`>>>`);
  console.log(`    urlParam: ${urlParam}`);

  try {
    const response = await fetch(urlParam);

    if (!response.ok) {
      throw new Error(`getJsonData() HTTP error! status: ${response.status}`);
    }

    const results = await response.json();

    return results;
  } catch (errorResponse) {
    logError(errorResponse.message);
  }
};

const base64Checker = (stringParam) => {
  const base64Regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

  return base64Regex.test(stringParam);
};

const stringQueryDecoder = (queryParam) => {
  let results;

  if (queryParam) {
    if (queryParam.startsWith('https')) {
      results = queryParam;
    } else if (base64Checker(queryParam)) {
      results = decodeURIComponent(atob(queryParam));
    } else {
      logDebug('Hook for alternative String Query scenarios');

      results = queryParam;
    }
  }

  return results;
};

// // Data Analyzer functions

const getDisplayMode = (searchParams) => {
  if (searchParams['mode'] && isValidBreakMode(searchParams['mode'])) {
    return searchParams['mode'];
  } else {
    return BreakModeEnum.DEFAULT;
  }
};

const getQueryStringScanTypeData = async (searchParam) => {
  let results = {
    mobileData: undefined,
    campaignData: undefined,
  };
  if (searchParam['mobile'] && searchParam['campaign']) {
    results = {
      mobileData: searchParam['mobile'],
      campaignData: searchParam['campaign'],
    };
  }

  return results;
};

const loadLocalJsonData = async () => {
  logDebug('Using Mock Data');
  const { default: localJson } = await import('@/assets/fake-data/localJson.js');

  // // TV Shopping Break local JSON
  // return localJson.tvShoppingBreakTwoSmallProductsLettermanJson;
  // return localJson.tvShoppingBreakEmojiPollAmgenJson;
  // return localJson.tvShoppingBreakPIPPollSamsungJson;
  // return localJson.tvShoppingBreakRightThirdScanParachuteHomeJson;
  // return localJson.tvShoppingBreakLowerHeroPollRoboVacJson;

  // // TV Shopping Stream local JSON
  // return localJson.tvShoppingStreamOneLowerDealLettermanJson;
  // return localJson.tvShoppingStreamOneLowerRightExpandableDealLettermanJson;
  // return localJson.tvShoppingStreamThreeLowerCycleDealLettermanJson;
  // return localJson.tvShoppingStreamProductHotspotsMarkRoberJson;
  // return localJson.tvShoppingStreamProductHotspotsTimingTestJson;
  return localJson.tvShoppingStreamTriviaPollWalkingDeadJson;
  // return localJson.tvShoppingStreampollStreamOverlayFoodAndWineJson;
  // return localJson.tvShoppingStreamPickSellerAndOfferingsMexicanRestaurantsJson;

  // // WiP local JSON
  // return localJson.mockTvDeeplinkJson;
};

// // Short URL-friendly keys → localJson.js exports. Lets local dev preview a
// // fixture via `?fixture=<key>` instead of hand-editing loadLocalJsonData()'s
// // hardcoded return or toggling App.vue between loadUrlJsonData/loadLocalJsonData.
export const FixtureRegistry = {
  'two-small-products': 'tvShoppingBreakTwoSmallProductsLettermanJson',
  'emoji-poll': 'tvShoppingBreakEmojiPollAmgenJson',
  'pip-poll': 'tvShoppingBreakPIPPollSamsungJson',
  'right-third-scan': 'tvShoppingBreakRightThirdScanParachuteHomeJson',
  'lower-hero-poll': 'tvShoppingBreakLowerHeroPollRoboVacJson',
  'one-lower-deal': 'tvShoppingStreamOneLowerDealLettermanJson',
  'one-lower-right-expandable-deal': 'tvShoppingStreamOneLowerRightExpandableDealLettermanJson',
  'three-lower-cycle-deal': 'tvShoppingStreamThreeLowerCycleDealLettermanJson',
  'product-hotspots': 'tvShoppingStreamProductHotspotsMarkRoberJson',
  'product-hotspots-timing-test': 'tvShoppingStreamProductHotspotsTimingTestJson',
  'trivia-poll': 'tvShoppingStreamTriviaPollWalkingDeadJson',
  'poll-stream-overlay': 'tvShoppingStreampollStreamOverlayFoodAndWineJson',
  'pick-seller-and-offerings': 'tvShoppingStreamPickSellerAndOfferingsMexicanRestaurantsJson',
};

/**
 * Loads a named fixture (see FixtureRegistry) straight from localJson.js.
 * Returns undefined if the key isn't registered, or if running in prod (dev-only escape
 * hatch — never let ?fixture= serve mock data to real users), so callers can fall back
 * to loadUrlJsonData.
 */
const loadNamedFixtureData = async (fixtureKey) => {
  if (appInstance === 'prod') {
    logError(`Fixture loading ("${fixtureKey}") is disabled in production`);
    return undefined;
  }

  const localJsonKey = FixtureRegistry[fixtureKey];
  if (!localJsonKey) {
    logError(
      `No fixture registered for "${fixtureKey}". Known keys: ${Object.keys(FixtureRegistry).join(', ')}`,
    );
    return undefined;
  }

  const { default: localJson } = await import('@/assets/fake-data/localJson.js');
  logDebug(`Using named fixture "${fixtureKey}" -> localJson.${localJsonKey}`);
  return localJson[localJsonKey];
};

const loadUrlJsonData = async (searchParam) => {
  if (searchParam['fixture']) {
    const fixtureResults = await loadNamedFixtureData(searchParam['fixture']);
    if (fixtureResults) return fixtureResults;
  }

  // const capturedData = stringQueryDecoder(searchParam['vast_data']);
  let capturedData;

  if (typeof searchParam === 'string') {
    capturedData = searchParam;
  } else {
    capturedData = stringQueryDecoder(searchParam['vast_data']);
  }

  console.log(' _-_-_-_-');
  console.log('Captured data ', capturedData);
  console.log('  ');

  logDebug('Captured data ', capturedData);
  const results = await getJsonData(capturedData);

  return results;
};

const loadQueryStringJsonData = async (searchParam) => {
  const capturedObject = separateQueryStringParameters(searchParam);
  const capturedData = stringQueryDecoder(capturedObject.vast_data);
  const results = await getJsonData(capturedData);

  return results;
};

const dataAnalyzers = {
  BreakModeEnum,
  FixtureRegistry,
  separateQueryStringParameters,
  getDisplayMode,
  getQueryStringScanTypeData,
  loadLocalJsonData,
  loadNamedFixtureData,
  loadUrlJsonData,
  loadQueryStringJsonData,
};

export default dataAnalyzers;
